import React, { useEffect, useState } from 'react';
import { LegadoEvent } from '../../types/Event';
import { getEvents, addEvent, updateEvent, deleteEvent } from '../../services/eventService';

const C = {
  navy: '#1B3A6B', navyLight: '#2A5298', gold: '#C8922A',
  green: '#27AE60', red: '#C0392B', bg: '#F5F3EE',
  bgCard: '#FFFFFF', text: '#2C2C2C', textMuted: '#6B6B6B',
  border: '#E5E0D5', inputBg: '#FAFAF8',
};

const emptyForm = () => ({
  title: '', date: '', time: '', location: '',
  topic: '', description: '', flyerUrl: '', isUpcoming: true,
});

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: `1.5px solid ${C.border}`, background: C.inputBg,
  fontSize: 14, color: C.text, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 5 }}>
      {label} {required && <span style={{ color: C.red }}>*</span>}
    </label>
    {children}
  </div>
);

const AdminEventsManager: React.FC = () => {
  const [events, setEvents] = useState<LegadoEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await getEvents();
      setEvents(data);
    } catch { setErrorMsg('Error cargando eventos.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const openEdit = (event: LegadoEvent) => {
    setEditingId(event.id!);
    setForm({
      title: event.title, date: event.date, time: event.time || '',
      location: event.location, topic: event.topic,
      description: event.description, flyerUrl: event.flyerUrl || '',
      isUpcoming: event.isUpcoming,
    });
    setShowForm(true);
    setErrorMsg('');
  };

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
    setErrorMsg('');
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!form.title || !form.date || !form.location || !form.topic || !form.description) {
      setErrorMsg('Por favor completa todos los campos requeridos.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      if (editingId) {
        await updateEvent(editingId, form);
        setSuccessMsg('✅ Evento actualizado.');
      } else {
        await addEvent(form);
        setSuccessMsg('✅ Evento creado.');
      }
      await load();
      handleCancel();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch { setErrorMsg('Error guardando el evento. Intenta de nuevo.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (event: LegadoEvent) => {
    if (!window.confirm(`¿Eliminar "${event.title}"?`)) return;
    try {
      await deleteEvent(event.id!);
      await load();
      setSuccessMsg('🗑️ Evento eliminado.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch { setErrorMsg('Error eliminando el evento.'); }
  };

  const filtered = events.filter((e) =>
    filter === 'all' || (filter === 'upcoming' ? e.isUpcoming : !e.isUpcoming)
  );

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.navy, fontFamily: 'Georgia, serif' }}>Gestión de Eventos</h2>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>
            {events.filter((e) => e.isUpcoming).length} próximos · {events.filter((e) => !e.isUpcoming).length} pasados
          </p>
        </div>
        <button onClick={openNew} style={{ background: C.navy, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          + Nuevo Evento
        </button>
      </div>

      {successMsg && <div style={{ background: '#D4EDDA', border: '1px solid #C3E6CB', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14, color: '#155724' }}>{successMsg}</div>}
      {errorMsg && <div style={{ background: '#F8D7DA', border: '1px solid #F5C6CB', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14, color: '#721C24' }}>{errorMsg}</div>}

      {showForm && (
        <div style={{ background: C.bgCard, border: `2px solid ${C.navy}`, borderRadius: 14, padding: '28px', marginBottom: 28, boxShadow: '0 8px 32px rgba(27,58,107,0.12)' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: C.navy, fontFamily: 'Georgia, serif' }}>
            {editingId ? '✏️ Editar Evento' : '➕ Nuevo Evento'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Título del evento" required>
                <input name="title" value={form.title} onChange={handleChange} style={inputStyle} placeholder="Ej: Taller de Educación Financiera" />
              </Field>
            </div>
            <Field label="Fecha" required>
              <input type="date" name="date" value={form.date} onChange={handleChange} style={inputStyle} />
            </Field>
            <Field label="Hora">
              <input name="time" value={form.time} onChange={handleChange} style={inputStyle} placeholder="Ej: 6:00 PM" />
            </Field>
            <Field label="Lugar / Plataforma" required>
              <input name="location" value={form.location} onChange={handleChange} style={inputStyle} placeholder="Ej: Zoom · Minneapolis, MN" />
            </Field>
            <Field label="Tema / Categoría" required>
              <input name="topic" value={form.topic} onChange={handleChange} style={inputStyle} placeholder="Ej: Educación Financiera" />
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Descripción" required>
                <textarea name="description" value={form.description} onChange={handleChange} rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} placeholder="Breve descripción del evento..." />
              </Field>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="URL del Flyer (opcional)">
                <input name="flyerUrl" value={form.flyerUrl} onChange={handleChange} style={inputStyle} placeholder="https://... (enlace a imagen del flyer)" />
              </Field>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Tipo de evento" required>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[{ val: true, label: '📅 Próximo' }, { val: false, label: '📚 Pasado' }].map(({ val, label }) => (
                    <button key={String(val)} type="button" onClick={() => setForm((f) => ({ ...f, isUpcoming: val }))}
                      style={{ padding: '8px 18px', borderRadius: 8, border: `2px solid ${form.isUpcoming === val ? C.navy : C.border}`, background: form.isUpcoming === val ? `${C.navy}15` : 'transparent', color: form.isUpcoming === val ? C.navy : C.textMuted, fontWeight: form.isUpcoming === val ? 700 : 500, fontSize: 13, cursor: 'pointer' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <button onClick={handleCancel} style={{ padding: '9px 20px', borderRadius: 8, border: `1.5px solid ${C.border}`, background: 'transparent', cursor: 'pointer', fontSize: 14, color: C.textMuted }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: saving ? '#ccc' : C.navy, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700 }}>
              {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear evento'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['all', 'upcoming', 'past'] as const).map((f) => {
          const labels = { all: 'Todos', upcoming: '📅 Próximos', past: '📚 Pasados' };
          return (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${filter === f ? C.navy : C.border}`, background: filter === f ? C.navy : 'transparent', color: filter === f ? '#fff' : C.textMuted, cursor: 'pointer', fontSize: 13, fontWeight: filter === f ? 700 : 500 }}>
              {labels[f]}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: C.textMuted }}>Cargando...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: C.bgCard, borderRadius: 12, padding: '40px 24px', textAlign: 'center', border: `2px dashed ${C.border}`, color: C.textMuted }}>
          No hay eventos en esta categoría.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((event) => (
            <div key={event.id} style={{ background: C.bgCard, borderRadius: 12, border: `1px solid ${C.border}`, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 6px rgba(27,58,107,0.05)' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: event.isUpcoming ? C.green : C.textMuted, flexShrink: 0 }} />
              <div style={{ minWidth: 90, fontSize: 13, color: C.textMuted, fontWeight: 600, flexShrink: 0 }}>{event.date}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.title}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{event.topic} · {event.location}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => openEdit(event)} style={{ padding: '6px 14px', borderRadius: 8, border: `1.5px solid ${C.navy}`, background: 'transparent', color: C.navy, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Editar</button>
                <button onClick={() => handleDelete(event)} style={{ padding: '6px 14px', borderRadius: 8, border: `1.5px solid ${C.red}`, background: 'transparent', color: C.red, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEventsManager;